import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { storageUsageService, StorageUsageSummary } from '../services/storageUsageService';
import { minutesUsageService, MinutesUsageSummary } from '../services/minutesUsageService';
import { formatFileSize, formatMinutes } from '../utils';
import DualCalendarDropdown from '../../../shared/components/ui/DualCalendarDropdown';

type FilterType = 'lifetime' | 'last7days' | 'last30days' | 'month' | 'custom';

const StorageUsagePage: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const [storageData, setStorageData] = useState<StorageUsageSummary | null>(null);
  const [minutesData, setMinutesData] = useState<MinutesUsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStorageExpanded, setIsStorageExpanded] = useState(true);
  const [isMinutesExpanded, setIsMinutesExpanded] = useState(true);
  
  // Date filter state
  const [filterType, setFilterType] = useState<FilterType>('lifetime');
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const dateButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!orgId) {
      setError('Organization ID is required');
      setIsLoading(false);
      return;
    }

    const loadUsageData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Calculate date range based on filter type
        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today
        
        let dateRange: { from: string | null; to: string | null } = { from: null, to: null };
        
        switch (filterType) {
          case 'lifetime':
            dateRange = { from: null, to: null };
            break;
          
          case 'last7days':
            const last7Days = new Date(today);
            last7Days.setDate(last7Days.getDate() - 7);
            last7Days.setHours(0, 0, 0, 0);
            dateRange = { from: last7Days.toISOString(), to: today.toISOString() };
            break;
          
          case 'last30days':
            const last30Days = new Date(today);
            last30Days.setDate(last30Days.getDate() - 30);
            last30Days.setHours(0, 0, 0, 0);
            dateRange = { from: last30Days.toISOString(), to: today.toISOString() };
            break;
          
          case 'month':
            const monthStart = new Date(selectedYear, selectedMonth - 1, 1);
            monthStart.setHours(0, 0, 0, 0);
            const monthEnd = new Date(selectedYear, selectedMonth, 0); // Last day of month
            monthEnd.setHours(23, 59, 59, 999);
            dateRange = { from: monthStart.toISOString(), to: monthEnd.toISOString() };
            break;
          
          case 'custom':
            dateRange = { from: dateFrom, to: dateTo };
            break;
        }
        
        const [storageResult, minutesResult] = await Promise.all([
          storageUsageService.getStorageUsageByOrg(orgId, dateRange.from, dateRange.to),
          minutesUsageService.getMinutesUsageByOrg(orgId, dateRange.from, dateRange.to),
        ]);
        setStorageData(storageResult);
        setMinutesData(minutesResult);
      } catch (err) {
        console.error('Error loading usage data:', err);
        setError('Failed to load usage data');
      } finally {
        setIsLoading(false);
      }
    };

    loadUsageData();
  }, [orgId, filterType, dateFrom, dateTo, selectedYear, selectedMonth]);

  // Handle quick filter selection
  const handleQuickFilter = (type: FilterType) => {
    setFilterType(type);
    if (type === 'lifetime') {
      setDateFrom(null);
      setDateTo(null);
      const now = new Date();
      setSelectedYear(now.getFullYear());
      setSelectedMonth(now.getMonth() + 1);
    } else if (type === 'month') {
      // Set to current month by default
      const now = new Date();
      setSelectedYear(now.getFullYear());
      setSelectedMonth(now.getMonth() + 1);
    }
  };

  // Calculate calendar position based on button position
  const calculateCalendarPosition = () => {
    if (dateButtonRef.current) {
      const rect = dateButtonRef.current.getBoundingClientRect();
      // Position below the button, aligned to the left edge
      setCalendarPosition({
        top: rect.bottom + 8,
        left: rect.left
      });
    }
  };

  // Handle custom date range selection
  const handleDateRangeSelect = (startDate: Date, endDate: Date) => {
    setFilterType('custom');
    setDateFrom(startDate.toISOString());
    // Set end date to end of day
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    setDateTo(end.toISOString());
    setIsCalendarOpen(false);
  };

  // Handle clear filter
  const handleClearFilter = () => {
    setFilterType('lifetime');
    setDateFrom(null);
    setDateTo(null);
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth() + 1);
    setIsCalendarOpen(false);
  };

  // Generate year options (current year and 5 years back)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 5; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  // Generate month options
  const getMonthOptions = () => {
    return [
      { value: 1, label: 'January' },
      { value: 2, label: 'February' },
      { value: 3, label: 'March' },
      { value: 4, label: 'April' },
      { value: 5, label: 'May' },
      { value: 6, label: 'June' },
      { value: 7, label: 'July' },
      { value: 8, label: 'August' },
      { value: 9, label: 'September' },
      { value: 10, label: 'October' },
      { value: 11, label: 'November' },
      { value: 12, label: 'December' },
    ];
  };

  // Format date range display
  const getDateRangeDisplay = (): string => {
    if (filterType === 'lifetime') return 'Lifetime';
    if (filterType === 'last7days') return 'Last 7 days';
    if (filterType === 'last30days') return 'Last 30 days';
    if (filterType === 'month') {
      const date = new Date(selectedYear, selectedMonth - 1, 1);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    }
    if (filterType === 'custom' && dateFrom && dateTo) {
      const start = new Date(dateFrom);
      const end = new Date(dateTo);
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
    return 'Select date range';
  };

  const { totalGB, categories: storageCategories } = storageData || { totalGB: 0, categories: [] };
  const { totalMinutes, categories: minutesCategories } = minutesData || { totalMinutes: 0, categories: [] };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Usage</h1>
        <p className="text-gray-600 mb-4">Overview of storage and minutes used by your organization</p>
        
        {/* Date Filter Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Custom Date Range - First */}
            <div className="relative flex items-center space-x-2">
              <button
                ref={dateButtonRef}
                onClick={() => {
                  if (!isCalendarOpen) {
                    calculateCalendarPosition();
                    setFilterType('custom');
                  }
                  setIsCalendarOpen(!isCalendarOpen);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center space-x-2 ${
                  filterType === 'custom'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title={getDateRangeDisplay()}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Custom Range</span>
              </button>
              
              {/* Calendar Dropdown - Rendered via Portal */}
              {isCalendarOpen && createPortal(
                <div 
                  className="fixed z-[9999]"
                  style={{
                    top: `${calendarPosition.top}px`,
                    left: `${calendarPosition.left}px`,
                    maxWidth: 'calc(100vw - 2rem)'
                  }}
                >
                  <DualCalendarDropdown
                    isOpen={isCalendarOpen}
                    onClose={() => setIsCalendarOpen(false)}
                    onDateRangeSelect={handleDateRangeSelect}
                    onClearFilter={handleClearFilter}
                    selectedStartDate={dateFrom ? new Date(dateFrom) : undefined}
                    selectedEndDate={dateTo ? new Date(dateTo) : undefined}
                    triggerRef={dateButtonRef}
                  />
                </div>,
                document.body
              )}
            </div>

            {/* Last 7 days - Second */}
            <button
              onClick={() => handleQuickFilter('last7days')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filterType === 'last7days'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last 7 days
            </button>

            {/* Last 30 days - Third */}
            <button
              onClick={() => handleQuickFilter('last30days')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filterType === 'last30days'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last 30 days
            </button>

            {/* Month Selector - Fourth */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleQuickFilter('month')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  filterType === 'month'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Month
              </button>
              {filterType === 'month' && (
                <>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getYearOptions().map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getMonthOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>

            {/* Current Filter Display */}
            {filterType !== 'lifetime' && (
              <div className="ml-auto text-sm text-gray-600">
                <span className="font-medium">Filter:</span> {getDateRangeDisplay()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Storage Usage Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        {/* Storage Header - Clickable to expand/collapse */}
        <button
          onClick={() => setIsStorageExpanded(!isStorageExpanded)}
          className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
          disabled={isLoading}
        >
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">Storage Usage</h2>
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-2xl font-bold text-gray-400">Loading...</span>
              </div>
            ) : (
              <span className="text-2xl font-bold text-gray-900">{totalGB.toFixed(2)} GB</span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${isStorageExpanded ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Storage Content - Expandable */}
        {isStorageExpanded && (
          <div className="px-6 pb-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500">Loading storage data...</div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-red-500">{error}</div>
              </div>
            ) : (
              <>
                {/* Segmented Progress Bar */}
                <div className="relative h-12 rounded-lg overflow-hidden bg-gray-100 mb-6">
              {storageCategories.map((category, index) => {
                const leftOffset = storageCategories.slice(0, index).reduce((sum, cat) => sum + cat.percentage, 0);
                return (
                  <div
                    key={category.category}
                    className="absolute top-0 bottom-0"
                    style={{
                      left: `${leftOffset}%`,
                      width: `${category.percentage}%`,
                      backgroundColor: category.color,
                      transition: 'all 0.3s ease',
                    }}
                    title={`${category.label}: ${formatFileSize(category.bytes)} (${category.percentage.toFixed(1)}%)`}
                  />
                );
              })}
              {storageCategories.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                  No storage data available
                </div>
              )}
            </div>

            {/* Storage Breakdown */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Storage Breakdown</h3>
              <div className="space-y-3">
                {storageCategories.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">No storage categories found</div>
                ) : (
                  storageCategories.map((category) => (
                    <div key={category.category} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: category.color }}
                        />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{category.label}</div>
                          <div className="text-xs text-gray-500">
                            {category.percentage.toFixed(1)}% of total storage
                          </div>
                        </div>
                      </div>
                      <div className="text-base font-semibold text-gray-900">
                        {formatFileSize(category.bytes)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Minutes Usage Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Minutes Header - Clickable to expand/collapse */}
        <button
          onClick={() => setIsMinutesExpanded(!isMinutesExpanded)}
          className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
          disabled={isLoading}
        >
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">Minutes Usage</h2>
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-2xl font-bold text-gray-400">Loading...</span>
              </div>
            ) : (
              <span className="text-2xl font-bold text-gray-900">{formatMinutes(totalMinutes)}</span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${isMinutesExpanded ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Minutes Content - Expandable */}
        {isMinutesExpanded && (
          <div className="px-6 pb-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500">Loading minutes data...</div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-red-500">{error}</div>
              </div>
            ) : (
              <>
                {/* Segmented Progress Bar */}
                <div className="relative h-12 rounded-lg overflow-hidden bg-gray-100 mb-6">
              {minutesCategories.map((category, index) => {
                const leftOffset = minutesCategories.slice(0, index).reduce((sum, cat) => sum + cat.percentage, 0);
                return (
                  <div
                    key={category.category}
                    className="absolute top-0 bottom-0"
                    style={{
                      left: `${leftOffset}%`,
                      width: `${category.percentage}%`,
                      backgroundColor: category.color,
                      transition: 'all 0.3s ease',
                    }}
                    title={`${category.label}: ${formatMinutes(category.minutes)} (${category.percentage.toFixed(1)}%)`}
                  />
                );
              })}
              {minutesCategories.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                  No minutes data available
                </div>
              )}
            </div>

            {/* Minutes Breakdown */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Minutes Breakdown</h3>
              <div className="space-y-3">
                {minutesCategories.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">No minutes categories found</div>
                ) : (
                  minutesCategories.map((category) => (
                    <div key={category.category} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: category.color }}
                        />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{category.label}</div>
                          <div className="text-xs text-gray-500">
                            {category.percentage.toFixed(1)}% of total minutes
                          </div>
                        </div>
                      </div>
                      <div className="text-base font-semibold text-gray-900">
                        {formatMinutes(category.minutes)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StorageUsagePage;

